import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/layout/Layout';
import QuickCapture from '../components/journal/QuickCapture';
import JournalEntry from '../components/journal/JournalEntry';
import DailyChallenge from '../components/journal/DailyChallenge';
import Button from '../components/common/Button';
import { sendMessageToPhilosopher, getDailyChallenge } from '../services/aiApi';
import { useAuth } from '../services/AuthContext';
import { trackFeatureUse } from '../services/analyticsService';

const JournalPage = () => {
  const { currentUser, updateUserProfile } = useAuth();
  const [entries, setEntries] = useState([]);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [view, setView] = useState('entries'); // 'entries', 'challenges'
  const [isLoading, setIsLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [currentPhilosopher, setCurrentPhilosopher] = useState('aristotle'); // Default philosopher
  const [pointsAwarded, setPointsAwarded] = useState(false);
  const [achievementUnlocked, setAchievementUnlocked] = useState(null);
  
  // Achievement definitions
  const achievements = [
    { 
      id: 'sophia_novice',
      name: 'Wisdom Novice', 
      description: 'Accumulated 5 Sophia Points on your philosophical journey',
      icon: '🔍',
      threshold: 5
    },
    {
      id: 'sophia_seeker',
      name: 'Wisdom Seeker',
      description: 'Accumulated 10 Sophia Points through philosophical inquiry',
      icon: '🦉',
      threshold: 10
    }
  ];

  // Check for unlocked achievements based on points
  const checkAchievements = (points) => {
    if (!currentUser || !currentUser.achievements) return null;
    
    // Get list of achievement IDs user already has
    const userAchievementIds = currentUser.achievements.map(a => a.id);
    
    // Find achievements that should be unlocked but user doesn't have yet
    const newAchievements = achievements.filter(achievement => 
      points >= achievement.threshold && 
      !userAchievementIds.includes(achievement.id)
    );
    
    return newAchievements.length > 0 ? newAchievements[0] : null;
  };
  
  // Function to award Sophia points to the user
  const awardSophiaPoints = (points = 1) => {
    if (currentUser) {
      // Calculate new points total
      const newTotal = (currentUser.stats.sophiaPoints || 0) + points;
      
      // Check for achievements
      const newAchievement = checkAchievements(newTotal);
      
      // Build updated user object
      let updatedUser = {
        ...currentUser,
        stats: {
          ...currentUser.stats,
          sophiaPoints: newTotal,
          entriesCount: (currentUser.stats.entriesCount || 0) + 1
        }
      };
      
      // Add achievement if earned
      if (newAchievement) {
        const achievementToAdd = {
          ...newAchievement,
          date: new Date().toISOString()
        };
        
        updatedUser = {
          ...updatedUser,
          achievements: [
            ...(currentUser.achievements || []),
            achievementToAdd
          ]
        };
        
        // Show achievement notification
        setAchievementUnlocked(newAchievement);
        
        // Clear achievement notification after a delay
        setTimeout(() => {
          setAchievementUnlocked(null);
        }, 8000);
      }
      
      // Update user profile
      updateUserProfile(updatedUser);
      setPointsAwarded(true);
      
      // Reset the points awarded flag after a few seconds
      setTimeout(() => {
        setPointsAwarded(false);
      }, 5000);
    }
  };
  
  // Force localStorage to save a value (sometimes helps with browser issues)
  const forceStore = (key, value) => {
    try {
      // First clear any existing item
      localStorage.removeItem(key);
      // Wait a tiny bit
      setTimeout(() => {
        // Then set the new item
        localStorage.setItem(key, value);
        console.log(`Force stored ${key}:`, value.slice(0, 50) + "...");
      }, 0);
    } catch (e) {
      console.error("Force store failed:", e);
    }
  };

  // Load entries on component mount and when currentUser changes
  useEffect(() => {
    // Only load journal entries if user is authenticated
    if (currentUser) {
      // Use a user-specific key for journal entries - make it distinctive
      const userEmail = currentUser.email || 'default';
      const userEntriesKey = `journal_entries_for_${userEmail}`;
      
      try {
        // Load entries from localStorage
        const savedEntries = localStorage.getItem(userEntriesKey);
        console.log(`Loading entries for ${userEmail}, found:`, savedEntries ? "data" : "nothing");
        
        if (savedEntries) {
          try {
            const parsedEntries = JSON.parse(savedEntries);
            console.log("Parsed entries length:", parsedEntries.length);
            setEntries(parsedEntries);
          } catch (parseError) {
            console.error("Error parsing entries:", parseError);
            setEntries([]);
            // Try to repair by storing empty array
            forceStore(userEntriesKey, JSON.stringify([]));
          }
        } else {
          console.log("No entries found in localStorage, checking backup");
          
          // Try to load from session storage backup
          try {
            const backupEntries = sessionStorage.getItem("journal_entries_backup");
            if (backupEntries) {
              console.log("Found backup entries in session storage!");
              const parsedBackup = JSON.parse(backupEntries);
              setEntries(parsedBackup);
              
              // Immediately save the restored entries to localStorage
              forceStore(userEntriesKey, backupEntries);
              return;
            }
          } catch (backupError) {
            console.error("Error checking backup:", backupError);
          }
          
          console.log("No backup found either, starting with empty array");
          setEntries([]);
          // Initialize storage with empty array
          forceStore(userEntriesKey, JSON.stringify([]));
        }
      } catch (error) {
        console.error('Error accessing localStorage:', error);
        setEntries([]);
      }
    } else {
      // Clear entries for non-authenticated users
      setEntries([]);
    }
    
    // Load daily challenge
    try {
      const savedChallenge = localStorage.getItem('dailyChallenge');
      
      if (savedChallenge) {
        setDailyChallenge(JSON.parse(savedChallenge));
      } else {
        // If no challenge exists, create a new one
        createNewDailyChallenge();
      }
    } catch (e) {
      console.error("Error loading challenge:", e);
      createNewDailyChallenge();
    }
  }, [currentUser]);
  
  // Save entries to localStorage when updated - use user-specific key
  useEffect(() => {
    if (currentUser && entries) {
      try {
        const userEmail = currentUser.email || 'default';
        const userEntriesKey = `journal_entries_for_${userEmail}`;
        
        // Store entries in localStorage using our forced approach
        const entriesJson = JSON.stringify(entries);
        console.log(`Saving ${entries.length} entries for ${userEmail}`);
        
        // Use our special force store mechanism
        forceStore(userEntriesKey, entriesJson);
        
        // Double-check storage worked
        setTimeout(() => {
          const checkStorage = localStorage.getItem(userEntriesKey);
          if (!checkStorage) {
            console.error("Storage verification failed - trying emergency backup");
            localStorage.setItem(userEntriesKey, entriesJson);
          }
        }, 100);
      } catch (error) {
        console.error('Error saving entries to localStorage:', error);
        // Try one more desperate approach - session storage as backup
        try {
          sessionStorage.setItem("journal_entries_backup", JSON.stringify(entries));
        } catch (e) {
          console.error("Even backup storage failed:", e);
        }
      }
    }
  }, [entries, currentUser]);
  
  // Save challenge to localStorage when updated
  useEffect(() => {
    if (dailyChallenge) {
      localStorage.setItem('dailyChallenge', JSON.stringify(dailyChallenge));
    }
  }, [dailyChallenge]);
  
  const createNewDailyChallenge = async () => {
    try {
      const newChallenge = await getDailyChallenge();
      setDailyChallenge(newChallenge);
    } catch (error) {
      console.error('Error creating new challenge:', error);
      // Fallback to static challenge
      setDailyChallenge({
        id: 'challenge1',
        title: 'The Socratic Examination',
        description: 'Identify one belief you hold strongly. Now, question it thoroughly. What evidence supports it? What might challenge it? Can you see it from another perspective?',
        difficulty: 'Moderate',
        category: 'Critical Thinking',
        philosophy: 'Socrates believed that the unexamined life is not worth living. By questioning our beliefs, we can separate truth from mere opinion and achieve greater wisdom.'
      });
    }
  };
  
  const handleSaveCapture = async (content, type = 'reflection') => {
    setIsLoading(true);
    
    // Track the journal entry creation for analytics
    trackFeatureUse('journal_entry_created');
    
    try {
      // Get AI insight using Anthropic's API
      const philosopherResponse = await sendMessageToPhilosopher(
        currentPhilosopher,
        `Please provide a very brief philosophical insight (maximum 1-2 sentences, no more than 30 words total) on this journal entry: "${content}"`,
        []
      );
      
      const aiInsight = philosopherResponse.response;
      
      const newEntry = {
        id: `entry-${Date.now()}`,
        title: type === 'question' ? 'Philosophical Question' : 
               type === 'insight' ? 'Philosophical Insight' : 
               'Daily Reflection',
        timestamp: new Date().toISOString(),
        content: content,
        type: currentPhilosopher === 'oracle' ? 'oracle' : 'philosophical',
        philosopher: currentPhilosopher.charAt(0).toUpperCase() + currentPhilosopher.slice(1),
        aiInsight: aiInsight,
        tags: [type]
      };
      
      // Update entries state with new entry
      const updatedEntries = [newEntry, ...entries];
      setEntries(updatedEntries);
      
      // Also directly save to localStorage to ensure sync
      if (currentUser) {
        try {
          const userEmail = currentUser.email || 'default';
          const userEntriesKey = `journal_entries_for_${userEmail}`;
          const entriesJson = JSON.stringify(updatedEntries);
          
          // Use forced storage for reliability
          forceStore(userEntriesKey, entriesJson);
        } catch (error) {
          console.error('Error in direct save to localStorage:', error);
        }
      }
      
      // Award Sophia points for journal entry
      awardSophiaPoints(1);
      
      setIsLoading(false);
      setShowOverlay(false);
    } catch (error) {
      console.error('Error getting AI insight:', error);
      
      // Fallback without AI insight if the API call fails
      const newEntry = {
        id: `entry-${Date.now()}`,
        title: type === 'question' ? 'Philosophical Question' : 'Daily Reflection',
        timestamp: new Date().toISOString(),
        content: content,
        type: 'philosophical',
        tags: [type]
      };
      
      // Update entries state with new entry
      const updatedEntries = [newEntry, ...entries];
      setEntries(updatedEntries);
      
      // Also directly save to localStorage to ensure sync
      if (currentUser) {
        try {
          const userEmail = currentUser.email || 'default';
          const userEntriesKey = `journal_entries_for_${userEmail}`;
          const entriesJson = JSON.stringify(updatedEntries);
          
          // Use forced storage for reliability
          forceStore(userEntriesKey, entriesJson);
        } catch (error) {
          console.error('Error in fallback save to localStorage:', error);
        }
      }
      
      // Award Sophia points for journal entry even on fallback
      awardSophiaPoints(1);
      
      setIsLoading(false);
      setShowOverlay(false);
    }
  };
  
  const handleSaveReflection = (entryId, reflection) => {
    const updatedEntries = entries.map(entry => 
      entry.id === entryId ? { ...entry, reflection } : entry
    );
    
    setEntries(updatedEntries);
    
    // Directly save to localStorage
    if (currentUser) {
      try {
        const userEmail = currentUser.email || 'default';
        const userEntriesKey = `journal_entries_for_${userEmail}`;
        const entriesJson = JSON.stringify(updatedEntries);
        
        // Use forced storage for reliability
        forceStore(userEntriesKey, entriesJson);
      } catch (error) {
        console.error('Error in reflection save to localStorage:', error);
      }
    }
  };
  
  const handleDeleteEntry = (entryId) => {
    const updatedEntries = entries.filter(entry => entry.id !== entryId);
    
    setEntries(updatedEntries);
    
    // Directly save to localStorage
    if (currentUser) {
      try {
        const userEmail = currentUser.email || 'default';
        const userEntriesKey = `journal_entries_for_${userEmail}`;
        const entriesJson = JSON.stringify(updatedEntries);
        
        // Use forced storage for reliability
        forceStore(userEntriesKey, entriesJson);
      } catch (error) {
        console.error('Error in delete save to localStorage:', error);
      }
    }
  };
  
  const handleCompleteChallenge = async (challengeId, response) => {
    setIsLoading(true);
    
    // Track the challenge completion for analytics
    trackFeatureUse('daily_challenge_completed');
    
    try {
      // Get AI insight on the challenge response
      const philosopherResponse = await sendMessageToPhilosopher(
        currentPhilosopher,
        `This is a response to the philosophical challenge "${dailyChallenge.title}": "${response}". Please provide a very brief philosophical insight (maximum 1-2 sentences, no more than 30 words total) on this response.`,
        []
      );
      
      const aiInsight = philosopherResponse.response;
      
      // Create a journal entry from the challenge response
      const newEntry = {
        id: `entry-challenge-${Date.now()}`,
        title: `Challenge: ${dailyChallenge.title}`,
        timestamp: new Date().toISOString(),
        content: response,
        type: 'philosophical',
        philosopher: currentPhilosopher.charAt(0).toUpperCase() + currentPhilosopher.slice(1),
        aiInsight: aiInsight,
        tags: ['Challenge', dailyChallenge.category]
      };
      
      // Update entries state with new entry
      const updatedEntries = [newEntry, ...entries];
      setEntries(updatedEntries);
      
      // Also directly save to localStorage to ensure sync
      if (currentUser) {
        try {
          const userEmail = currentUser.email || 'default';
          const userEntriesKey = `journal_entries_for_${userEmail}`;
          const entriesJson = JSON.stringify(updatedEntries);
          
          // Use forced storage for reliability
          forceStore(userEntriesKey, entriesJson);
        } catch (error) {
          console.error('Error in challenge save to localStorage:', error);
        }
      }
      
      // Award Sophia points for completing a challenge
      awardSophiaPoints(2); // Challenges are worth more points
      
      // Mark the challenge as completed
      setDailyChallenge({
        ...dailyChallenge,
        completed: true,
        response: response
      });
      
      // Create a new challenge for tomorrow
      setTimeout(() => {
        createNewDailyChallenge();
      }, 86400000); // 24 hours
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error getting AI insight:', error);
      
      // Fallback without AI insight
      setDailyChallenge({
        ...dailyChallenge,
        completed: true,
        response: response
      });
      
      setIsLoading(false);
    }
  };
  
  const handlePhilosopherChange = (philosopherId) => {
    setCurrentPhilosopher(philosopherId);
  };
  
  // Add an event listener for beforeunload to ensure storage is synced
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentUser && entries && entries.length > 0) {
        try {
          // Force one last save before page unloads
          const userEmail = currentUser.email || 'default';
          const userEntriesKey = `journal_entries_for_${userEmail}`;
          localStorage.setItem(userEntriesKey, JSON.stringify(entries));
          
          // Also save to sessionStorage as a backup
          sessionStorage.setItem("journal_entries_backup", JSON.stringify(entries));
        } catch (e) {
          console.error("Error in final save:", e);
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [entries, currentUser]);
  

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <h1 className="text-3xl font-serif font-bold text-white mb-4 md:mb-0">
              Your Philosophical Journal
            </h1>
            <div className="flex space-x-1">
              <button
                onClick={() => setView('entries')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  view === 'entries' ? 'bg-oliveGold text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                Journal
              </button>
              <button
                onClick={() => setView('challenges')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  view === 'challenges' ? 'bg-oliveGold text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                Challenges
              </button>
              <Button
                variant="secondary"
                onClick={() => setShowOverlay(true)}
              >
                + New Entry
              </Button>
            </div>
          </div>

          {/* Philosopher selector */}
          <div className="mb-6 p-4 rounded-lg border border-white/10" style={{ background: '#0f1d32' }}>
            <h3 className="text-sm font-medium text-white/40 mb-2">Choose Your Philosophical Guide</h3>
            <div className="flex flex-wrap gap-2">
              {['socrates', 'plato', 'aristotle', 'heraclitus', 'pythagoras', 'xenophon'].map(philosopher => (
                <button
                  key={philosopher}
                  onClick={() => handlePhilosopherChange(philosopher)}
                  className={`px-3 py-1.5 text-sm rounded-full transition ${
                    currentPhilosopher === philosopher
                      ? 'bg-oliveGold text-white'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  {philosopher.charAt(0).toUpperCase() + philosopher.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Main content */}
          <AnimatePresence mode="wait">
            {view === 'entries' ? (
              <motion.div
                key="entries"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl font-serif font-semibold text-white/80 mb-4">
                  Journal Entries
                </h2>

                {entries.length > 0 ? (
                  entries.map((entry, index) => (
                    <JournalEntry
                      key={entry.id}
                      entry={entry}
                      onSaveReflection={handleSaveReflection}
                      onDelete={handleDeleteEntry}
                      initiallyExpanded={index === 0 && new Date(entry.timestamp) > new Date(Date.now() - 300000)}
                    />
                  ))
                ) : (
                  <div className="text-center py-12 rounded-lg border border-white/10" style={{ background: '#0f1d32' }}>
                    <h3 className="text-lg font-medium text-oliveGold/80 mb-3 font-serif">Your Journal Awaits</h3>
                    <p className="text-white/40 mb-6 max-w-md mx-auto">
                      Begin your philosophical journey by capturing your thoughts, questions, or insights.
                    </p>
                    <Button onClick={() => setShowOverlay(true)}>
                      Create Your First Entry
                    </Button>
                  </div>
                )}

                {/* Points notification — toast style */}
                <AnimatePresence>
                  {pointsAwarded && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="fixed top-20 right-6 z-30"
                    >
                      <div className="bg-oliveGold/10 border border-oliveGold/30 text-oliveGold rounded-lg p-4 shadow-lg flex items-center backdrop-blur-sm">
                        <span className="text-2xl mr-3">✨</span>
                        <div>
                          <p className="font-medium text-base">+1 Sophia Point</p>
                          <p className="text-sm text-oliveGold/70">For philosophical journaling</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Achievement notification — toast style */}
                <AnimatePresence>
                  {achievementUnlocked && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="fixed top-20 right-6 z-30"
                    >
                      <div className="bg-philosophicalPurple/10 border border-philosophicalPurple/30 text-philosophicalPurple rounded-lg p-4 shadow-lg backdrop-blur-sm">
                        <div className="flex items-center mb-1">
                          <div className="text-2xl mr-3">{achievementUnlocked.icon}</div>
                          <h4 className="font-serif font-semibold">Achievement Unlocked!</h4>
                        </div>
                        <p className="font-medium text-sm">{achievementUnlocked.name}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ) : (
              <motion.div
                key="challenges"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl font-serif font-semibold text-white/80 mb-4">
                  Daily Challenge
                </h2>

                {dailyChallenge && (
                  <DailyChallenge
                    challenge={dailyChallenge}
                    onCompleteChallenge={handleCompleteChallenge}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      {/* Quick capture overlay — parchment modal on dark backdrop */}
      <AnimatePresence>
        {showOverlay && (
          <motion.div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !isLoading && setShowOverlay(false)}
          >
            <motion.div
              className="rounded-xl w-full max-w-lg shadow-2xl overflow-hidden"
              style={{ background: '#f8f5ea' }}
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-xl font-serif font-semibold text-aegeanBlue">
                    Capture Your Thoughts
                  </h2>
                  <button
                    onClick={() => !isLoading && setShowOverlay(false)}
                    className="text-aegeanBlue/50 hover:text-aegeanBlue"
                    disabled={isLoading}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <QuickCapture
                  onSave={handleSaveCapture}
                  isLoading={isLoading}
                  philosopher={currentPhilosopher}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="rounded-lg p-6 shadow-xl flex items-center space-x-4" style={{ background: '#0f1d32' }}>
              <div className="flex space-x-1">
                <div className="w-2 h-2 rounded-full bg-oliveGold animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-oliveGold animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-oliveGold animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <p className="text-oliveGold/80 font-serif">
                Consulting with {currentPhilosopher.charAt(0).toUpperCase() + currentPhilosopher.slice(1)}...
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default JournalPage;